/**
 * Thin fetch wrapper that mirrors PSB's `bffFetch`: always sends cookies
 * (the BFF session cookie is HttpOnly so we must `credentials: include`)
 * and conditionally adds the Duende-mandated `X-CSRF: 1` header on
 * authenticated BFF endpoints. The wrapper deliberately leaves login,
 * logout, and OIDC callback URLs alone — those are full-page navigations
 * that the IdP/BFF reject if a CSRF header is present.
 */

// Endpoints that should never receive CSRF from this wrapper. The
// login/logout entries are defensive only — today the sole sign-in/out paths
// are `useAuth().signIn()` / `signOut()`, which do a full-page
// `window.location.assign` and never route through `bffFetch`. They stay
// listed so a future `bffFetch("/bff/auth/logout", …)` can't accidentally
// attach a CSRF header that the BFF rejects on a top-level navigation. (Don't
// grep for callers and assume it's dead code — it's a guard, not a path.)
const NO_CSRF_PREFIXES = [
  "/bff/auth/login",
  "/bff/auth/logout",
  "/signin-oidc",
  "/signout-callback-oidc",
];

// Endpoints that always require CSRF — Duende rejects them otherwise.
const ALWAYS_CSRF_PREFIXES = ["/bff/auth/user", "/bff/api/"];

function extractPath(input: string): string {
  try {
    if (input.startsWith("http://") || input.startsWith("https://")) {
      const url = new URL(input);
      return `${url.pathname}${url.search}`;
    }
  } catch {
    // Fall back to raw input for malformed URLs.
  }
  return input;
}

function isUnsafeMethod(method: string): boolean {
  return !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
}

function hasPrefix(path: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => path.startsWith(prefix));
}

function getRequestMeta(input: RequestInfo | URL, init: RequestInit) {
  const method =
    init.method ?? (input instanceof Request ? input.method : "GET");
  if (typeof input === "string") return { path: extractPath(input), method };
  if (input instanceof URL)
    return { path: `${input.pathname}${input.search}`, method };
  if (input instanceof Request) return { path: extractPath(input.url), method };
  return { path: "", method };
}

function requiresCsrf(path: string, method: string): boolean {
  if (!path) return false;
  if (hasPrefix(path, NO_CSRF_PREFIXES)) return false;
  if (hasPrefix(path, ALWAYS_CSRF_PREFIXES)) return true;
  return path.startsWith("/bff/") && isUnsafeMethod(method);
}

/**
 * BFF-aware fetch wrapper.
 *
 * Always includes credentials. Auto-adds `X-CSRF: 1` for authenticated
 * BFF endpoints; never on login/logout/OIDC callback paths. Returns the
 * raw Response — callers handle 401s themselves (typically by treating
 * the session as expired and bouncing through `/bff/auth/login`).
 */
export async function bffFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  const { path, method } = getRequestMeta(input, init);

  if (requiresCsrf(path, method) && !headers.has("X-CSRF")) {
    headers.set("X-CSRF", "1");
  }

  return fetch(input, {
    ...init,
    credentials: "include",
    headers,
  });
}
